// © Microsoft Corporation. All rights reserved.

// Imported from Windows team
// https://microsoft.visualstudio.com/Universal%20Store/_git/MEE.Shell.Dashboard?path=%2Fsrc%2FApp%2FClientApp%2Fapp%2FwidgetRuntime%2Fapis%2Fapi.interfaces.ts&_a=contents&version=GBmaster
// All "public" types visible to widgets
// TODO: Import from npm once available as package

// API names
export type ApiName =
    "settings" // IWidgetSettings
    | "status" // IWidgetStatus
    | "visibility" // IWidgetVisibility
    | "authenticator" // IWidgetAuthenticator
    | "lifecycle" // IWidgetLifecycle
    | "properties" // IWidgetProperties
    | "sizes"; // IWidgetSize

// Global version of this set of api contracts, use this in the target version of all tryGetApi requests
export const widgetApiVersion = 1;
export interface IWidgetStatus {
    showLastUpdated(date: Date): void;
    hideLastUpdated(): void;
}

export type WidgetSettingsEvent =
    "onShowSettingsStart" // Widgets is starting the show settings experience
    | "onShowSettingsEnd" // Widgets show settings experience completed
    | "onHideSettingsStart" // Widgets is starting the hide settings experience
    | "onHideSettingsEnd"; // Widgets hide settings experience completed

export interface IWidgetSettings {
    showSettings(): void;
    hideSettings(): void;

    addEventListener(listener: (eventName: WidgetSettingsEvent) => void): void;
    removeEventListener(listener: (eventName: WidgetSettingsEvent) => void): void;
}

export interface IWidgetAuthenticator {
    // Props available are values such as:
    //  AccountId, UPN, DisplayName, OID, Authority, FirstName, LastName,  MUID, ProviderAuthority (i.e. "consumers"|"organizations")
    getProperty(name: string): Promise<string>;
    getToken(clientId: string, scope: string, resource: string, apiVersion: string): Promise<string>;
}

export type WidgetVisibilityEvent =
    "visible" // The widget has become visible in the viewport
    | "notvisible"; // The widget was visible and is now not visible in the viewport.

export interface IWidgetVisibility {
    addEventListener(listener: (eventName: WidgetVisibilityEvent) => void): void;
    removeEventListener(listener: (eventName: WidgetVisibilityEvent) => void): void;

    isVisible(): boolean;
}

export type WidgetLifecycleEvent =
    // The widget has been resumed. This will be sent any time the widget is resumed, which can include when
    // when the widget is resumed because the host is being shown or because the host is unsuspending in the background.
    "resumed"
    // The widget is being given an opportunity to perform a content refresh. This can be sent to notify th widget
    // that a background refresh is occurring and it should take the opportunity to refresh its content if it needs
    // to. The returned promise should be completed when the widget is done with its refresh.
    | "contentrefresh"
    // The widget is about to be suspended. This will be sent any time the widget is about to be suspended, which can include
    // when the widget host was shown and has now been hidden or when the host is re-suspending after a background refresh. The returned promise
    // should complete when the widget is ready to suspend.
    | "suspending"
    // The widget is about to be removed from the users widget grid.
    | "removing";

export interface IWidgetLifecycle {
    addEventListener(listener: (eventName: WidgetLifecycleEvent) => Promise<void>): void;
    removeEventListener(listener: (eventName: WidgetLifecycleEvent) => Promise<void>): void;
}

export type WidgetProperty =
    "sessionId" // string: unique id changed for each user session
    | "title" // string: displayed in upper left corner of widget attribution area
    | "attribution" // string: "Powered By" statement
    | "hostVersion" // string: host version
    | "animationsEnabled" // boolean: Settings->Display->Show Animations on Windows
    | "advancedEffectsEnabled" // boolean: Settings->Display->Show transparency on Windows
    | "autohideScrollbars" // boolean: Settings->Display->auto hide scrollbars
    | "colorMode" // string: light | dark
    | "accentColor" // string: six digit hex "0a0a0a"
    | "region" // string: Two digit ISO 3166-2 Windows region code
    | "activationUrl" // string: URI to activate when widget attribution area is clicked
    | "displayLocale" // string: Full Windows display locale
    | "offline" // boolean: true if widgets host detects the device is offline
    | "accentColorRamp";

export interface IWidgetProperties {
    title: string; // Settable by the widget to dynamically alter the widget title.
    attribution: string; // Settable by the widget for the "Powered by" statement.
    activationUrl: string | null; // Settable by the widget for activation link

    getProperty(name: WidgetProperty): any;

    addPropertyChangeListener(listener: (name: WidgetProperty) => void): void;
    removePropertyChangeListener(listener: (name: WidgetProperty) => void): void;
}

export type WidgetSizeEvent = "onSizeChangeStart" | "onSizeChangeEnd";
export type WidgetSize = "large" | "medium" | "small";

export interface IWidgetSize {
    getSize(): WidgetSize;

    addEventListener(listener: (eventName: WidgetSizeEvent, size: WidgetSize) => void): void;
    removeEventListener(listener: (eventName: WidgetSizeEvent) => void): void;
}

export interface IWidgetInstance {
    readonly widgetId: string;
    readonly instanceId: number;

    // When calling this method, specify both minVersion and targetVersion to be "widgetApiVersion"
    // minVersion may be specified to be an earlier version if there is a behavior
    //     dependency that is known and supported on the api object requested.
    tryGetApi<T>(apiName: ApiName, minVersion: number, targetVersion: number): T | null;
}
